import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import logoImg from '../../assets/logo.png';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { getFailoverAwareAuth, getFailoverAwareDatabase } from '../../config/firebaseWithFailover';
import { doc, getDoc, setDoc, collection, query, where, limit, getDocs } from 'firebase/firestore';
import styles from './Login.module.css';
import modalStyles from '../../components/DocumentModal.module.css';
import PrivacyPolicy from '../../components/common/PrivacyPolicy';
import ConsentManager from '../../components/common/ConsentManager';
import Terms from '../../components/common/Terms';
import RefundPolicy from '../../components/common/RefundPolicy';
import AccessibilityStatement from '../../components/common/AccessibilityStatement';
import { determineUserTypeForLogin } from '../../utils/userTypeUtils';
import { navigateUserToPortalAfterLogin } from '../../utils/loginNavigation';
import Logger from '../../services/logger';
import { AUTH_PAGE_THEME_VARS } from '../../constants/authPageTheme';
import { APPROVAL_STATUS, APPROVAL_STATUS_LOGIN_MESSAGE } from '../../constants/registrationState';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [error, setError] = useState('');
  const [show2FAModal, setShow2FAModal] = useState(false);
  const [twoFACode, setTwoFACode] = useState('');
  const [twoFAError, setTwoFAError] = useState('');
  const [twoFALoading, setTwoFALoading] = useState(false);
  const [pendingUser, setPendingUser] = useState<any>(null);

  useEffect(() => {
    const message = (location.state as { registrationMessage?: string } | null)?.registrationMessage;
    if (message) {
      setError(message);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.pathname, location.state, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setTwoFAError('');
    setShow2FAModal(false);
    setPendingUser(null);

    try {
      const email = formData.email;
      const password = formData.password;

      Logger.debug('Attempting login with:', { email, password: '***' });
      
      const auth = getFailoverAwareAuth();
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      Logger.debug('Firebase authentication successful:', { uid: user.uid });

      // Try to fetch Firestore user data from both collections
      let userData: any = null;
      try {
        // First try the new companyUsers collection
        const db = getFailoverAwareDatabase();
        const companyUserDoc = await getDoc(doc(db, 'companyUsers', user.uid));
        if (companyUserDoc.exists()) {
          userData = companyUserDoc.data();
          Logger.debug('User data fetched from companyUsers:', {
            uid: user.uid,
            userType: userData.userType,
            role: userData.role,
            companyUserRole: userData.companyUserRole,
            isAdmin: userData.isAdmin,
            isSuperAdmin: userData.isSuperAdmin
          });
        } else {
          // Fallback: Check legacy users collection
          Logger.warn('User not found in companyUsers collection, checking legacy users collection:', { uid: user.uid });
          const legacyUserDoc = await getDoc(doc(db, 'users', user.uid));
          if (legacyUserDoc.exists()) {
            userData = legacyUserDoc.data();
            Logger.debug('User data fetched from legacy users collection:', {
              uid: user.uid,
              userType: userData.userType,
              role: userData.role
            });
          }
        }
        
        if (!userData) {
          setError('User profile not found. Please register your account.');
          return;
        }
      } catch (firestoreErr) {
        Logger.error('Firestore error during login:', firestoreErr as Error);
        setError('Could not fetch user profile. Please contact support.');
        return;
      }

      // Check approval status
      if (userData.approvalStatus === APPROVAL_STATUS.PENDING) {
        setError(APPROVAL_STATUS_LOGIN_MESSAGE[APPROVAL_STATUS.PENDING]);
        return;
      }

      if (userData.approvalStatus === APPROVAL_STATUS.REJECTED) {
        setError(APPROVAL_STATUS_LOGIN_MESSAGE[APPROVAL_STATUS.REJECTED]);
        return;
      }

      // 2FA logic
      if (userData && userData["2faEnabled"]) {
        setShow2FAModal(true);
        setPendingUser({ user, userData });
        setTwoFACode('');
        setTwoFAError('');
        setTwoFALoading(true);
        // Call backend to send code
        try {
          await fetch('/api/auth/send-2fa-code', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email: user.email })
          });
        } catch (err) {
          setTwoFAError('Failed to send 2FA code. Please try again.');
        }
        setTwoFALoading(false);
        return;
      }

      // Normal navigation - Check admin status FIRST
      // Check for admin via isAdmin/isSuperAdmin flags OR userType === 'admin'
      if (userData && (userData.isAdmin || userData.isSuperAdmin || userData.userType === 'admin')) {
        // Admin authentication - highest priority
        Logger.debug('Navigating to admin dashboard', {
          isAdmin: userData.isAdmin,
          isSuperAdmin: userData.isSuperAdmin,
          userType: userData.userType
        });
        navigate('/admin');
      } else if (userData) {
        const userTypeResult = determineUserTypeForLogin(userData);

        Logger.debug('User type determination result:', {
          userType: userTypeResult.userType,
          confidence: userTypeResult.confidence,
          source: userTypeResult.source,
          originalUserType: userData.userType,
          role: userData.role,
          companyUserRole: userData.companyUserRole,
          hasCarrierOperations: userData.hasCarrierOperations
        });

        const navResult = navigateUserToPortalAfterLogin(userTypeResult, navigate);
        if (!navResult.ok) {
          Logger.error('Login blocked — user type undetermined:', new Error(navResult.error), {
            uid: user.uid,
            email: user.email,
            userTypeResult
          });
          setError(navResult.error);
        }
      } else {
        setError('User data not found. Please contact support.');
      }
    } catch (err) {
      Logger.error('Login error:', err as Error);
      setError('Invalid email or password');
    }
  };

  const handle2FAVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setTwoFAError('');
    setTwoFALoading(true);
    try {
      const res = await fetch('/api/auth/verify-2fa-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: pendingUser.user.email, code: twoFACode })
      });
      const data = await res.json();
      if (!res.ok) {
        setTwoFAError(data.error || 'Invalid code.');
        setTwoFALoading(false);
        return;
      }
      // Success: proceed to dashboard - Check admin status FIRST
      // Check for admin via isAdmin/isSuperAdmin flags OR userType === 'admin'
      if (pendingUser.userData.isAdmin || pendingUser.userData.isSuperAdmin || pendingUser.userData.userType === 'admin') {
        Logger.debug('Navigating to admin dashboard (2FA)', {
          isAdmin: pendingUser.userData.isAdmin,
          isSuperAdmin: pendingUser.userData.isSuperAdmin,
          userType: pendingUser.userData.userType
        });
        navigate('/admin');
      } else {
        const userTypeResult = determineUserTypeForLogin(pendingUser.userData);
        const navResult = navigateUserToPortalAfterLogin(userTypeResult, navigate);
        if (!navResult.ok) {
          setError(navResult.error);
          setShow2FAModal(false);
          setTwoFALoading(false);
          return;
        }
      }
      setShow2FAModal(false);
      setTwoFALoading(false);
    } catch (err) {
      setTwoFAError('Failed to verify code.');
      setTwoFALoading(false);
    }
  };

  return (
    <>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>Login to Tranzit.io - Freight Logistics Platform</title>
        <meta name="title" content="Login to Tranzit.io - Freight Logistics Platform" />
        <meta name="description" content="Sign in to your Tranzit.io account to access your freight logistics dashboard, manage loads, and connect with carriers, brokers, and shippers." />
        <meta name="keywords" content="Tranzit.io login, freight logistics login, carrier portal login, broker portal login, shipper portal login" />
        <link rel="canonical" href="https://tranzit.io/login" />
        
        {/* Performance Optimization - Resource Hints */}
        <link rel="dns-prefetch" href="https://fonts.googleapis.com" />
        <link rel="dns-prefetch" href="https://fonts.gstatic.com" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://tranzit.io/login" />
        <meta property="og:title" content="Login to Tranzit.io" />
        <meta property="og:description" content="Sign in to your Tranzit.io account to access your freight logistics dashboard." />
        <meta property="og:image" content="https://tranzit.io/logo.png" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:url" content="https://tranzit.io/login" />
        <meta name="twitter:title" content="Login to Tranzit.io" />
        <meta name="twitter:description" content="Sign in to your Tranzit.io account to access your freight logistics dashboard." />
        <meta name="twitter:image" content="https://tranzit.io/logo.png" />
      </Helmet>
      
    <div className={styles.loginContainer} style={AUTH_PAGE_THEME_VARS}>
      <header className={styles.header}>
        <Link to="/" className={styles.logo}>
          <img src={logoImg} alt="Tranzit.io Logo" className={styles.logoImg} loading="eager" width="200" height="50" />
          <div className={styles.textLogo}>
            <span>T</span>
            <span>R</span>
            <span>A</span>
            <span>N</span>
            <span>Z</span>
            <span>I</span>
            <span>T</span>
          </div>
        </Link>
        <nav className={styles.nav}>
          <Link className={styles.menuLink} to="/">Home</Link>
          <Link className={styles.menuLink} to="/technology">Technology</Link>
          <Link className={styles.menuLink} to="/about">About Us</Link>
          <Link className={styles.menuLink} to="/register">Register</Link>
        </nav>
      </header>
      <div className={styles.loginContent}>
        <div className={styles.loginBox}>
        <h1>Welcome to Tranzit</h1>
        
        {error && <div className={styles.error}>{error}</div>}
        
        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.inputGroup}>
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className={styles.input}
              placeholder="Enter your email (or leave empty for test account)"
            />
          </div>

          <div className={styles.inputGroup}>
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              className={styles.input}
              placeholder="Enter your password (or leave empty for test account)"
            />
          </div>

          <div className={styles.rememberMe}>
            <label>
              <input
                type="checkbox"
                checked={formData.rememberMe}
                onChange={(e) => setFormData({ ...formData, rememberMe: e.target.checked })}
              />
              Remember me
            </label>
          </div>

          <button type="submit" className={styles.loginButton}>
            Login
          </button>

          <button 
            type="button" 
            className={styles.registerButton}
            onClick={() => navigate('/register')}
          >
            Register
          </button>

          <div className={styles.forgotPasswordWrap}>
            <Link to="/forgot-password" className={styles.linkForgot}>
              Forgot Password?
            </Link>
          </div>

          {process.env.NODE_ENV === 'development' && (
            <div className={styles.devNote}>
              <small>
                Please enter your email and password to sign in.
              </small>
            </div>
          )}
        </form>
        <div className={styles.privacyInlineWrap}>
          <PrivacyPolicy />
        </div>
        </div>
      </div>
      {show2FAModal && (
        <div className={modalStyles.modalOverlay}>
          <div className={modalStyles.modal}>
            <h2>Two-Factor Authentication</h2>
            <form onSubmit={handle2FAVerify}>
              <div style={{ marginBottom: 16 }}>
                <label htmlFor="twofa-code">Enter the 6-digit code sent to your email:</label>
                <input
                  id="twofa-code"
                  type="text"
                  value={twoFACode}
                  onChange={e => setTwoFACode(e.target.value)}
                  maxLength={6}
                  style={{ width: '100%', padding: 8, fontSize: 18, marginTop: 8 }}
                  autoFocus
                />
              </div>
              {twoFAError && <div style={{ color: 'red', marginBottom: 8 }}>{twoFAError}</div>}
              <button type="submit" className={modalStyles.uploadButton} disabled={twoFALoading || twoFACode.length !== 6}>
                {twoFALoading ? 'Verifying...' : 'Verify'}
              </button>
              <button type="button" className={modalStyles.closeButton} onClick={() => setShow2FAModal(false)}>
                Cancel
              </button>
            </form>
          </div>
        </div>
      )}
      <footer className={styles.footer}>
        <div className={styles.footerLinks}>
          <PrivacyPolicy />
          <ConsentManager />
          <Terms />
          <RefundPolicy />
          <AccessibilityStatement />
        </div>
        <p className={styles.footerCopyright}>&copy; {new Date().getFullYear()} TRANZIT. All rights reserved.</p>
      </footer>
    </div>
    </>
  );
};

export default Login; 